import { useQuery } from '@tanstack/react-query';
import { apiBase } from '@/lib/apiClient';
import type { LoginBrand } from '@/components/auth/useLoginForm';
import type { LoginTemplateId } from '@/components/auth/loginTemplates';

/**
 * TravelERP's own addresses. They always show the TravelERP sign-in, so they are not even asked about;
 * every other address is a company's own domain and is looked up.
 */
const TRAVELERP_HOSTS = ['localhost', '127.0.0.1'];
const TRAVELERP_HOST_SUFFIXES = ['.goerp.live'];

export function isTravelErpHost(host: string): boolean {
  const h = host.toLowerCase();
  return TRAVELERP_HOSTS.includes(h) || h === 'goerp.live' || TRAVELERP_HOST_SUFFIXES.some((s) => h.endsWith(s));
}

export interface LoginPage {
  brand: LoginBrand | null;
  template: LoginTemplateId;
  /** Which of the company's addresses this is (Website, Erp, Vendor, B2B, Customer). */
  portal?: string;
  /** False for a portal not built yet: the page says it is coming instead of offering a sign-in. */
  portalBuilt?: boolean;
  /** The company code, given on the vendor portal address (its sign-in names the company on every call). */
  companyCode?: string | null;
}

const TRAVELERP_PAGE: LoginPage = { brand: null, template: 'Skyline' };

interface LoginPageResponse {
  companyName: string;
  logoFileId?: number | null;
  primaryColor: string;
  loginTemplate?: LoginTemplateId;
  portal?: string;
  portalBuilt?: boolean;
  companyCode?: string | null;
}

/**
 * Which sign-in page this address shows. On a company's verified domain: that company's name, colour,
 * logo and chosen design. Anywhere else — or if the lookup fails — the TravelERP page.
 *
 * Asked with a bare fetch on purpose: no session and no remembered company code is sent, so only the
 * address decides (the API answers this path by ?host= alone).
 */
export function useLoginPage() {
  const host = window.location.hostname;
  const lookup = !isTravelErpHost(host);

  return useQuery({
    queryKey: ['login-page', host],
    enabled: lookup,
    staleTime: 10 * 60_000,
    retry: false,
    initialData: lookup ? undefined : TRAVELERP_PAGE,
    queryFn: async (): Promise<LoginPage> => {
      const q = `host=${encodeURIComponent(host)}`;
      try {
        const res = await fetch(`${apiBase}/public/login-page?${q}`, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) return TRAVELERP_PAGE;
        const body = (await res.json()) as { data?: LoginPageResponse };
        const d = body.data;
        if (!d?.companyName) return TRAVELERP_PAGE;
        return {
          brand: {
            companyName: d.companyName,
            primaryColor: d.primaryColor,
            logoUrl: d.logoFileId ? `${apiBase}/public/login-page/logo?${q}` : null,
          },
          template: d.loginTemplate ?? 'Skyline',
          portal: d.portal,
          portalBuilt: d.portalBuilt ?? true,
          companyCode: d.companyCode ?? null,
        };
      } catch {
        return TRAVELERP_PAGE;
      }
    },
  });
}
